import React from 'react';
import { Button } from './ui/button';
import { HandHeart, Award, Users, Mail, ArrowRight, CheckCircle } from 'lucide-react';

const GetInvolved = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const ways = [
    {
      icon: HandHeart,
      title: 'Volunteer',
      description: 'Be the boots on the ground that make a difference',
      details: [
        'Door-to-door canvassing and voter outreach',
        'Phone banking to connect with community members',
        'Help organize campaign events and rallies',
        'Distribute yard signs and campaign materials',
        'Join us at community events and town halls',
      ],
      color: 'purple',
    },
    {
      icon: Award,
      title: 'Endorse',
      description: 'Stand with us and let your voice be heard',
      details: [
        'Add your name to our list of public supporters',
        'Share why you support Wanda on social media',
        'Speak at community forums and events',
        'Help us build momentum and credibility',
        'Join business and community leaders backing our vision',
      ],
      color: 'amber',
    },
    {
      icon: Users,
      title: 'Host an Event',
      description: 'Bring the campaign to your neighborhood',
      details: [
        'Host a meet-and-greet in your home or business',
        'Organize a neighborhood coffee with Wanda',
        'Plan a fundraising event for the campaign',
        'Invite friends and neighbors to join the movement',
        'We\'ll provide materials and support',
      ],
      color: 'rose',
    },
    {
      icon: Mail,
      title: 'Spread the Word',
      description: 'Help us reach every corner of Kissimmee',
      details: [
        'Share our campaign on social media',
        'Display a yard sign at your home or business',
        'Forward our emails to friends and family',
        'Talk to neighbors about our vision',
        'Help us reach voters who share our values',
      ],
      color: 'emerald',
    },
  ];

  const colorClasses = {
    purple: { bg: 'bg-purple-600', hover: 'hover:bg-purple-700', light: 'bg-purple-50', text: 'text-purple-700' },
    amber: { bg: 'bg-amber-600', hover: 'hover:bg-amber-700', light: 'bg-amber-50', text: 'text-amber-700' },
    rose: { bg: 'bg-rose-600', hover: 'hover:bg-rose-700', light: 'bg-rose-50', text: 'text-rose-700' },
    emerald: { bg: 'bg-emerald-600', hover: 'hover:bg-emerald-700', light: 'bg-emerald-50', text: 'text-emerald-700' },
  };

  return (
    <section id="get-involved" className="relative py-20 md:py-32 overflow-hidden">
      {/* Background with Wanda's Image */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900/95 via-purple-800/90 to-purple-900/95"></div>
        <img
          src="https://customer-assets.emergentagent.com/job_c8ba1316-fe9a-4786-bbb1-586136be868e/artifacts/3bloytv3_Elegance%20By%20AG-2554.jpg"
          alt="Wanda Roman"
          className="w-full h-full object-cover opacity-30"
        />
      </div>

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Join Our Movement
          </h2>
          <div className="w-24 h-1.5 bg-amber-400 mx-auto mb-8"></div>
          <p className="text-xl md:text-2xl text-white/95 leading-relaxed mb-6">
            This campaign is about all of us coming together to create positive change in Kissimmee. Every voice matters. Every action counts. Every person makes a difference.
          </p>
          <p className="text-lg text-purple-200 italic">
            "Change doesn't happen from the top down. It happens from the ground up, when people like you decide to get involved and make their voices heard."
          </p>
          <p className="text-sm text-purple-300 mt-2">— Wanda Roman</p>
        </div>

        {/* Ways to Get Involved */}
        <div className="grid md:grid-cols-2 gap-8 max-w-7xl mx-auto mb-12">
          {ways.map((way, index) => {
            const Icon = way.icon;
            const colors = colorClasses[way.color];
            
            return (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-2xl overflow-hidden hover:scale-105 transition-all duration-300"
              >
                <div className={`${colors.bg} p-6`}>
                  <div className="flex items-center gap-4">
                    <div className="bg-white/20 backdrop-blur-sm w-16 h-16 rounded-xl flex items-center justify-center">
                      <Icon className="w-8 h-8 text-white" strokeWidth={2.5} />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white mb-1">
                        {way.title}
                      </h3>
                      <p className="text-white/90 text-sm">
                        {way.description}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="p-6 space-y-3">
                  {way.details.map((detail, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <CheckCircle className={`w-5 h-5 ${colors.text} flex-shrink-0 mt-0.5`} />
                      <p className="text-gray-700 text-sm leading-relaxed">{detail}</p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Call to Action */}
        <div className="text-center max-w-3xl mx-auto">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 md:p-12 border border-white/20">
            <h3 className="text-3xl font-bold text-white mb-4">
              Ready to Make a Difference?
            </h3>
            <p className="text-lg text-white/90 mb-8">
              Whether you have five minutes or five hours a week, there's a place for you in this campaign. Let's work together to build the Kissimmee we all deserve.
            </p>
            <Button
              onClick={scrollToContact}
              size="lg"
              className="bg-amber-500 hover:bg-amber-600 text-purple-900 font-bold text-xl px-12 py-7 shadow-2xl hover:shadow-amber-500/50 transition-all duration-300 hover:scale-105 group"
            >
              Get Started Today
              <ArrowRight className="ml-2 w-6 h-6 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GetInvolved;
